import { memo, useMemo } from "react";
import { Tabs, Spin, Button, Space, Badge, Alert, Typography } from "antd";
import { WarningOutlined, FullscreenOutlined } from "@ant-design/icons";
import styles from "../styles/UberSyncUI.module.css";

const { Text } = Typography;
const { TabPane } = Tabs;

const ConflictsPanel = ({ previewLoading, previewExclusions, previewDiscrepancies, onOpenDrawer }) => {
  const categoryExclusions = useMemo(() => 
    previewExclusions ? previewExclusions.filter(ex => ex.type === "category") : []
  , [previewExclusions]);

  const productExclusions = useMemo(() => 
    previewExclusions ? previewExclusions.filter(ex => ex.type === "product") : []
  , [previewExclusions]);

  const discrepancies = previewDiscrepancies || [];
  const badgeCount = (previewExclusions ? previewExclusions.length : 0) + discrepancies.length;

  return (
    <div className={styles.conflictPanel}>
      <div className={styles.titleArea}>
        <span className={styles.titleText}>
          <WarningOutlined className={styles.warningIconTitleColor} /> Conflictos y Exclusiones
        </span>
        <Space>
          {badgeCount > 0 && (
            <Badge count={badgeCount} style={{ backgroundColor: '#fa541c' }} />
          )}
          <Button
            type="text"
            icon={<FullscreenOutlined />}
            onClick={onOpenDrawer}
            title="Ampliar vista de conflictos"
          />
        </Space>
      </div>

      {previewLoading ? (
        <div className={styles.spinnerContainer}>
          <Spin size="large" tip="Cargando exclusiones..." />
        </div>
      ) : (
        <Tabs defaultActiveKey={discrepancies.length > 0 ? "discrepancies" : "categories"} className={styles.tabContainer}>
          {/* Pestaña Discrepancias Uber */}
          <TabPane
            tab={
              <span className={`${styles.tabTitle} ${discrepancies.length > 0 ? styles.tabTitleWarning : ""}`}>
                Discrepancias API ({discrepancies.length})
              </span>
            }
            key="discrepancies"
            className={styles.tabPaneWrapper}
          >
            <div className={styles.exclusionsContainer}>
              {discrepancies.length > 0 ? (
                discrepancies.map((disc, idx) => (
                  <div key={idx} className={styles.exclusionItem}>
                    <Space align="start">
                      <WarningOutlined className={styles.warningIconColor} />
                      <div>
                        <Text strong className={styles.branchNameText}>
                          {disc.type === 'MISSING_MODIFIER_ITEM' ? `Item no encontrado: ${disc.missingItemId}` : `Item sin precio: ${disc.itemId}`}
                        </Text>
                        <div className={styles.exclusionReason}>{disc.message}</div>
                      </div>
                    </Space>
                  </div>
                ))
              ) : (
                <Alert
                  message="Sin discrepancias de formato"
                  description="El payload a enviar está limpio y todos los condimentos referenciados existen."
                  type="success"
                  showIcon
                />
              )}
            </div>
          </TabPane>

          {/* Pestaña Categorías */}
          <TabPane
            tab={
              <span className={`${styles.tabTitle} ${categoryExclusions.length > 0 ? styles.tabTitleWarning : ""}`}>
                Categorías ({categoryExclusions.length})
              </span>
            }
            key="categories"
            className={styles.tabPaneWrapper}
          >
            <div className={styles.exclusionsContainer}>
              {categoryExclusions.length > 0 ? (
                categoryExclusions.map((ex, idx) => (
                  <div key={idx} className={styles.exclusionItem}>
                    <Space align="start">
                      <WarningOutlined className={styles.warningIconColor} />
                      <div>
                        <Text strong className={styles.branchNameText}>
                          {ex.name}
                        </Text>
                        <div className={styles.exclusionReason}>{ex.reason}</div>
                      </div>
                    </Space>
                  </div>
                ))
              ) : (
                <Alert
                  message="Sin advertencias para categorías"
                  description="Todas las categorías se encuentran habilitadas."
                  type="success"
                  showIcon
                />
              )}
            </div>
          </TabPane>

          {/* Pestaña Productos */}
          <TabPane
            tab={
              <span className={`${styles.tabTitle} ${productExclusions.length > 0 ? styles.tabTitleWarning : ""}`}>
                Productos ({productExclusions.length})
              </span>
            }
            key="products"
            className={styles.tabPaneWrapper}
          >
            <div className={styles.exclusionsContainer}>
              {productExclusions.length > 0 ? (
                productExclusions.map((ex, idx) => (
                  <div key={idx} className={styles.exclusionItem}>
                    <Space align="start">
                      <WarningOutlined className={styles.warningIconColor} />
                      <div>
                        <Text strong className={styles.branchNameText}>
                          {ex.name}
                        </Text>
                        <div><Text type="secondary" className={styles.skuText}>SKU: {ex.sku} | Categoría: {ex.categoryName}</Text></div>
                        <div className={styles.exclusionReason}>{ex.reason}</div>
                      </div>
                    </Space>
                  </div>
                ))
              ) : (
                <Alert
                  message="Sin advertencias para productos"
                  description="Todos los productos activos están disponibles para sincronización."
                  type="success"
                  showIcon
                />
              )}
            </div>
          </TabPane>
        </Tabs>
      )}
    </div>
  );
};

export default memo(ConflictsPanel);
